"use client";

import * as React from "react";

import { OTPInput, OTPInputContext } from "input-otp";
import { LuMinus } from "react-icons/lu";

import { cn } from "@/app/_libs/utils/cn";

function InputOTP({
  className,
  containerClassName,
  ...props
}: React.ComponentPropsWithoutRef<typeof OTPInput> & {
  containerClassName?: string;
}) {
  return (
    <OTPInput
      data-slot="input-otp"
      containerClassName={cn("flex items-center gap-2 has-disabled:opacity-50", containerClassName)}
      className={cn("disabled:cursor-not-allowed", className)}
      {...props}
    />
  );
}

function InputOTPGroup({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div data-slot="input-otp-group" className={cn("flex items-center", className)} {...props} />
  );
}

function InputOTPSlot({
  index,
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div"> & {
  index: number;
}) {
  // Cast to avoid React type duplication issues across workspaces
  type OTPContextShape = { slots?: Array<{ char?: string; hasFakeCaret?: boolean; isActive?: boolean }> };
  const inputOTPContext = React.useContext(OTPInputContext as unknown as React.Context<OTPContextShape>);
  const slot = inputOTPContext?.slots?.[index] as
    | { char?: string; hasFakeCaret?: boolean; isActive?: boolean }
    | undefined;
  const { char, hasFakeCaret, isActive } = slot ?? {};

  return (
    <div
      data-slot="input-otp-slot"
      data-active={isActive}
      className={cn(
        "relative flex text-body-normal/md h-12.5 w-25.5 rounded-lg items-center justify-center   transition-all outline-none border border-outline-600 data-[active=true]:z-10 bg-background-0  text-text-600",
        className,
      )}
      {...props}>
      {char ?? (isActive ? "" : ".")}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="animate-caret-blink bg-foreground h-4 w-px duration-1000" />
        </div>
      )}
    </div>
  );
}

function InputOTPSeparator({ ...props }: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div data-slot="input-otp-separator" role="separator" {...props}>
      <LuMinus />
    </div>
  );
}

export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot };
